import { util, appletMessaging } from "@ax/office-shared-lib";
import { AccountInfo } from "@azure/msal-browser";
import { Authenticator } from "./Authenticator";

export namespace appState {

    /** Holds the current state of the application. */
    export let State: appState.ApplicationState<appState.AddinSettings>;

    /** The Apps for Office node containing the server interaction pages. */
    export const appsForOfficeNode = "/OfficeApps";

    export interface Authentication {

        /** The current tenant populated in workbook generation. */
        Tenant?: string;
    }

    export interface AddinSettings {

        /** The name of the host to which the add-in should connect. */
        HostName?: string;

        /** The property bag containing applets that should be loaded, keyed by applet ID with the values as the corresponding applet settings. */
        Applets?: util.PropertyBag<any>;

        /** The ID of the applet that should be made active after applet load is complete. */
        ActiveAppletId?: string;

        /** The language the add-in should run within. */
        Language?: string;

        /** The header values to apply. */
        Headers?: util.PropertyBag<string>;

        /** The unique ID of the document. */
        DocumentId?: string;

        /** The date the workbook was created. */
        DateCreated?: string;

        /** The ID of the activity used to generated the document. */
        GenerationActivityId?: string;

        /** The authentication object containing the tenant information. */
        Authentication?: Authentication;
    }

    export interface ServerConfiguration {

        /** The optional headers to apply on all data calls. */
        Headers?: util.PropertyBag<ConfigurableHeaderDetails>;

        /** The key for the Application Insights instance of which to write telemetry. */
        AppInsightsKey?: string;

        /** The type of authentication the server supports (e.g. Cookie or OAuth). */
        AuthType?: AuthType;

        /** The authentication realm used to authenticate a user via OAuth. */
        AuthRealm?: string;

        /** The ID of the authentication tenant used to authenticate a user via OAuth. */
        AuthTenantId?: string;

        /** The ID of the authentication client used to authenticate a user via OAuth. */
        AuthClientId?: string;

        /** The ID of the authentication resource used to authenticate a user via OAuth. */
        AuthResourceId?: string;

        /** The URL of the data endpoint. */
        DataEndpointUrl?: string;

        /** The URL of the label endpoint. */
        LabelEndpointUrl?: string;

        /** The registrations for the applets to be loaded. */
        AppletRegistrations?: AppletRegistration[];

        /** The registrations for the resources to be loaded. */
        ResourceRegistrations?: ResourceRegistration[];

        /** The redirect url for getting authenticated configuration. */
        RedirectUrl?: string;

        /** Array of features to enable. */
        Features?: string[];

        /** The maximum number of data entries to publish in one batch. */
        DataPublishBatchLimit?: number;

        /** Learn more link shown in the error list */
        LearnMoreUrl?: string;
    }

    export interface ConfigurableHeaderDetails {

        /** The name that is displayed for the header in the options dialog. */
        DisplayName: string;

        /** The default value for the header. */
        Value: string;

        /** The valid values for the header. */
        ValidValues?: string[];

        /** The valid labels for the header. */
        ValidLabels?: string[];

        /** If true, indicates that the header should not be displayed in the options dialog */
        IsHidden?: boolean;
    }

    export interface AppletRegistration {

        /** The ID of the registered applet. */
        AppletID?: string;
    }

    export interface RelativeAppletRegistration extends AppletRegistration {

        /** The name or name label ID of the registered applet. */
        Name: string;

        /** The description or description label ID of the registered applet. */
        Description: string;

        /** The relative URL of the registered applet. */
        RelativeUrl: string;

        /** The applications (bit flag) that the applet supports. */
        SupportedApplications: number;

        /** Indicates the applet on which the applet is dependent. */
        DependentOn: string;
    }

    export interface CommonAppletRegistration extends AppletRegistration {

        /** The common version of the registered applet. */
        CommonVersion?: number;
    }

    export interface ResourceRegistration {

        /** The ID of the registered resource. */
        ResourceID?: string;
    }

    export interface RelativeResourceRegistration extends ResourceRegistration {

        /** The relative URL of the registered resource. */
        RelativeUrl?: string;
    }

    export interface CommonResourceRegistration extends ResourceRegistration {

        /** The common version of the registered resource. */
        CommonVersion?: number;
    }

    /* jshint -W069 */
    /** The supported authentication types. */
    export enum AuthType {

        /** 
         * Represents cookie based authentication.
         * @deprecated No longer functions
         */
        Cookie = <any>"Cookie",

        /** Represents OAuth based authentication. */
        OAuth = <any>"OAuth"
    }

    /** The different states of authentication. */
    export enum AuthenticationState {

        /** No settings have been applied. */
        uninitialized = <any>"uninitialized",

        /** The user is not authenticated. */
        notAuthenticated = <any>"notAuthenticated",

        /** Waiting for the user to authenticate (e.g. the user has clicked the 'sign in' link). */
        waitingUserAuthentication = <any>"waitingUserAuthentication",

        /** The user is authenticating. */
        authenticating = <any>"authenticating",

        /** The user is authenticated. */
        authenticated = <any>"authenticated",

        /** The user is signing out. */
        signingOut = <any>"signingOut",
    }

    /** The different states of authorization. */
    export enum AuthorizationState {

        /** Unsure whether the user is authorized or not. */
        unknown = <any>"unknown",

        /** The user is clearly not authorized. */
        notAuthorized = <any>"notAuthorized",
    }

    /** The available applets ready states, indicating how many ready applets the app has. */
    export enum AppletsReadyState {

        /** State indicating the app has no ready applets. */
        None = <any>"none",

        /** State indicating the app has a single ready applet. */
        Single = <any>"single",

        /** State indicating the app has more than one ready applets. */
        Multiple = <any>"multiple"
    }
    /* jshint +W069 */

    /** Represents details about an authenticated user. */
    export interface UserDetails {

        /** The friendly name to display for the user (e.g. John Doe). */
        displayName: string;

        /** The user name (e.g. john.doe@contoso.com). */
        userName: string;

        /** The ID of the tenant (e.g. tenant ID GUID). */
        tenantID: string;

        /** The ID of the authenticated object (e.g. user ID GUID). */
        objectID: string;
    }

    /** Represents the state of the application. */
    export class ApplicationState<TSettings extends AddinSettings> {

        /** Indicates whether or not the app is initialized. */
        public IsInitialized: boolean;

        /** Indicates the state of authentication. */
        public AuthenticationState: appState.AuthenticationState;

        /** Indicates the state of authorization. */
        public AuthorizationState: appState.AuthorizationState;

        /** Indicates the number of applets that are ready. */
        public AppletsReadyState: appState.AppletsReadyState;

        /** Indicates if registered applets have been retrieved. */
        public RegistrationsRetrieved: boolean;

        /** Indicates the number of applets that are currently loading. */
        public AppletsLoadingCount: number;

        /** The settings object loaded from the file. */
        public Settings: TSettings;

        /** The features object loaded from the file. */
        public Features: util.AddinFeatures;

        /** The configuration object loaded from the server. */
        public ServerConfig: ServerConfiguration;

        /** A property bag of valid origins, with origin as the key and value. */
        public ValidOriginSet: any;

        /** The authentication context that is currently active. */
        public AuthContext: Authenticator;

        /** The current language. */
        public Language: string;

        /** The GUID used to identify an Office app session. */
        public CorrelationID: string;

        /** Indicates if manual sign in has been attempted. */
        public ManualSignInAttempted: boolean;

        /** The current application hosting the add-in. */
        public CurrentApplication: util.ApplicationType;

        /** The type of Office application instance hosting the add-in. */
        public ClientType: util.ClientType;

        /** The current version of Office hosting the app. */
        public OfficeVersion: util.OfficeVersion;

        /** Indicates if the enhanced server config has been successfully retrieved */
        public HasEnhancedConfigBeenRetrieved: boolean;

        constructor() {
            this.IsInitialized = false;
            this.AuthenticationState = appState.AuthenticationState.uninitialized;
            this.AuthorizationState = appState.AuthorizationState.unknown;
            this.AppletsReadyState = appState.AppletsReadyState.None;
            this.RegistrationsRetrieved = false;
            this.AppletsLoadingCount = 0;
            this.ManualSignInAttempted = false;
            this.ClientType = window.parent === window ? util.ClientType.Desktop : util.ClientType.Web;
            this.HasEnhancedConfigBeenRetrieved = false;
        }

        /**
         * Clones the state.
         * @returns A clone of the state.
         */
        public clone(): ApplicationState<TSettings> {
            return $.extend(true, new appState.ApplicationState(), this);
        }

        /**
         * Gets the string that correlates to the current application type.
         * @returns The string that correlates to the current application type.
         */
        public getCurrentApplicationString(): string {
            return this.CurrentApplication === undefined ? "UNKNOWN" : util.ApplicationType[this.CurrentApplication];
        }

        /**
         * Gets the string that correlates to the client type.
         * @returns The string that correlates to the client type.
         */
        public getClientTypeString(): string {
            return util.ClientType[this.ClientType];
        }

        /**
         * Gets the add-in's connection, returning the URL of the direction connection.
         * @returns The add-in's connection.
         */
        public getConnection(): string {
            return this.Settings.HostName;
        }

        /**
         * Gets details about the authenticated user.
         * @returns Details about the authenticated user.
         */
        public getUserDetails(): UserDetails {
            let userDetails: UserDetails;
            if (this.AuthContext) {
                userDetails = appState.extractUserDetails(this.AuthContext.getActiveAccount());
            } else {
                userDetails = {
                    displayName: "",
                    userName: "",
                    tenantID: "",
                    objectID: ""
                };
            }
            return userDetails;
        }

        /**
         * Generates the list of scopes needed for login.
         * @returns The list of scopes needed for login.
         */
        public getMsalLoginScopes(): string[] {
            const ret: string[] = ["openid", "profile"];

            if (this.ServerConfig?.AuthResourceId) {
                ret.push(this.ServerConfig.AuthResourceId + "/.default");
            } else {
                ret.push(localStorage.msalResourceID + "/.default");
            }

            return ret;
        }

        /**
         * Generates the list of scopes needed for token acquisition.
         * @returns The list of scopes needed for token acquisition.
         */
        public getMsalTokenScopes(): string[] {
            if (this.ServerConfig?.AuthResourceId) {
                return [this.ServerConfig.AuthResourceId + "/.default"];
            }

            return [localStorage.msalResourceID + "/.default"];
        }
    }

    /**
     * Extracts the details about the provided user. If the user or profile values aren't present, empty values are
     * provided in the details. An object is always returned.
     * @param user The authenticated user.
     * @returns Details about the provided user.
     */
    export function extractUserDetails(user: AccountInfo): appState.UserDetails {
        let userDetails: appState.UserDetails;
        if (user) {
            userDetails = {
                displayName: user.name || user.username,
                userName: user.username,
                tenantID: user.tenantId || "",
                objectID: user.localAccountId || ""
            }
        } else {
            userDetails = {
                displayName: "",
                userName: "",
                tenantID: "",
                objectID: ""
            };
        }
        return userDetails;
    }
}
